import { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUp, ArrowDown } from 'lucide-react';
import { fmt, healthClass, isTotalRow } from '@/utils/formatters';
import { useIsMobile } from '@/hooks/useIsMobile';

export interface DrillTableProps {
  title: string;
  columns?: string[];
  data: Record<string, unknown>[];
  /** 当前条件下总行数（服务端截断时 > data.length） */
  total?: number;
  onCustomerClick?: (customerName: string, row: Record<string, unknown>) => void;
  onCellClick?: (column: string, value: unknown, row: Record<string, unknown>) => void;
  /** 翻到已加载数据末尾附近时触发，由上层做服务端 offset 分页加载 */
  onLoadMore?: () => Promise<void> | void;
  /** 点击表头排序：由后端全局排序并替换当前块数据 */
  onSort?: (column: string, direction: 'asc' | 'desc') => Promise<void> | void;
}

type SortState = {
  column: string | null;
  direction: 'asc' | 'desc';
};

const PERSON_COLS = ['人员', '姓名', '执行人'];

/** 根据表格标题识别下钻列：提示文案与 export_to_api.py 里的 title 保持一致。
 * 不同 tab 的人员列名可能叫「人员」「姓名」或「执行人」，统一返回候选列名列表。
 * 兜底：所有非「明细」类汇总/排名/预警表，只要列里有省办/人员/姓名/执行人，都允许点击下钻。 */
function getDrillDownColumns(title: string, columns: string[]): string[] {
  const cols: string[] = [];
  const isDetail = /明细|每日/.test(title);
  const isSummary = /排名|汇总|预警/.test(title);

  // 明确的下钻提示
  if (title.includes('点击省办下钻')) cols.push('省办');
  if (title.includes('点击人员下钻到每日明细')) PERSON_COLS.forEach((c) => { if (!cols.includes(c)) cols.push(c); });
  if (title.includes('点击执行人查看每日明细')) PERSON_COLS.forEach((c) => { if (!cols.includes(c)) cols.push(c); });
  if (title.includes('点击人员查看负责客户')) PERSON_COLS.forEach((c) => { if (!cols.includes(c)) cols.push(c); });
  if (cols.length > 0) return cols;

  // 兜底：汇总/排名/预警表允许点击维度列
  if (!isDetail && isSummary) {
    if (columns.includes('省办')) cols.push('省办');
    PERSON_COLS.forEach((c) => { if (columns.includes(c) && !cols.includes(c)) cols.push(c); });
  }

  return cols;
}

const DESKTOP_PAGE_SIZE = 50;
const MOBILE_PAGE_SIZE = 20;
// 距离最后一页还有 2 页时开始预加载下一批，用户无感知
const PRELOAD_PAGES = 2;
// 触摸滑动翻页阈值（px）
const SWIPE_THRESHOLD = 50;

export default function DrillTable({
  title,
  columns,
  data,
  total,
  onCustomerClick,
  onCellClick,
  onLoadMore,
  onSort,
}: DrillTableProps) {
  const isMobile = useIsMobile();
  const PAGE_SIZE = isMobile ? MOBILE_PAGE_SIZE : DESKTOP_PAGE_SIZE;

  const [sort, setSort] = useState<SortState>({ column: null, direction: 'desc' });
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const touchStartX = useRef<number | null>(null);

  // 数据被整体替换（应用筛选 / 重新加载）时回到第 1 页；
  // 「加载更多」是追加（旧行引用保持不变），此时保持当前页，避免翻页被重置
  const prevDataRef = useRef<Record<string, unknown>[] | null>(null);
  useEffect(() => {
    const prev = prevDataRef.current;
    prevDataRef.current = data;
    if (!prev || prev.length === 0) return;
    const isAppend = data.length > prev.length && data[prev.length - 1] === prev[prev.length - 1];
    if (!isAppend) setPage(1);
  }, [data]);

  const tableColumns = useMemo(() => {
    if (columns && columns.length > 0) return columns;
    if (data.length === 0) return [];
    return Object.keys(data[0]);
  }, [columns, data]);

  const sortedData = useMemo(() => {
    // 后端排序：data 已经是全局排序后的结果，不再本地二次排序
    if (onSort) return data;
    if (!sort.column) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sort.column!];
      const bVal = b[sort.column!];

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sort.direction === 'asc' ? aVal - bVal : bVal - aVal;
      }

      const aStr = String(aVal ?? '');
      const bStr = String(bVal ?? '');
      return sort.direction === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr);
    });
  }, [data, sort, onSort]);

  const totalCount = total ?? data.length;
  const truncated = total !== undefined && total > data.length;
  const totalPages = Math.max(1, Math.ceil(sortedData.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageRows = sortedData.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // 服务端 offset 分页：接近已加载数据末尾时自动加载下一批
  useEffect(() => {
    if (!onLoadMore || !truncated || loadingMore) return;
    if (safePage >= totalPages - PRELOAD_PAGES) {
      let cancelled = false;
      setLoadingMore(true);
      Promise.resolve(onLoadMore()).finally(() => {
        if (!cancelled) setLoadingMore(false);
      });
      return () => {
        cancelled = true;
      };
    }
  }, [safePage, totalPages, truncated, onLoadMore, loadingMore]);

  const handleSort = async (column: string) => {
    const direction = sort.column === column && sort.direction === 'desc' ? 'asc' : 'desc';

    // 有后端排序能力时，优先让后端全局排序并替换数据
    if (onSort) {
      setSort({ column, direction });
      setPage(1);
      await onSort(column, direction);
      return;
    }

    // 兜底：本地对已加载数据排序
    setSort({ column, direction });
    setPage(1);
  };

  const drillDownCols = useMemo(() => getDrillDownColumns(title, tableColumns), [title, tableColumns]);

  // 移动端左右滑动翻页
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current == null || totalPages <= 1) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) < SWIPE_THRESHOLD) return;
    if (diff > 0 && safePage < totalPages) {
      setPage((p) => Math.min(totalPages, p + 1));
    } else if (diff < 0 && safePage > 1) {
      setPage((p) => Math.max(1, p - 1));
    }
    touchStartX.current = null;
  };

  const renderCell = (column: string, value: unknown, row: Record<string, unknown>) => {
    const text = fmt(value, column);
    const tagClass = healthClass(String(value), column);

    // 客户名称可点击
    if (column === '客户名称' && onCustomerClick && value) {
      return (
        <span className="customer-link" onClick={() => onCustomerClick(String(value), row)}>
          {text}
        </span>
      );
    }

    // 下钻列可点击（省办 / 人员 / 姓名 / 执行人）
    if (drillDownCols.includes(column) && onCellClick && value) {
      return (
        <span className="customer-link" onClick={() => onCellClick(column, value, row)}>
          {text}
        </span>
      );
    }

    // 有健康度标签
    if (tagClass) {
      return <span className={`tag ${tagClass}`}>{text}</span>;
    }

    return <span>{text}</span>;
  };

  if (data.length === 0) {
    return (
      <div className="section">
        <div className="section-title">{title}</div>
        <div className="empty">暂无数据</div>
      </div>
    );
  }

  return (
    <div
      className="section"
      onTouchStart={isMobile ? handleTouchStart : undefined}
      onTouchEnd={isMobile ? handleTouchEnd : undefined}
    >
      <div className="section-title">{title}</div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              {tableColumns.map((col) => (
                <th key={col} onClick={() => handleSort(col)}>
                  {col}
                  {sort.column === col && (
                    <span className="sort-icon">{sort.direction === 'desc' ? <ArrowDown size={12} /> : <ArrowUp size={12} />}</span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row, idx) => (
              <tr key={idx} className={isTotalRow(row) ? 'total' : ''}>
                {tableColumns.map((col) => (
                  <td key={col}>{renderCell(col, row[col], row)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="table-footer">
        <span className="table-count">
          {truncated ? (
            <>
              已加载 {data.length.toLocaleString('zh-CN')} / 共 {totalCount.toLocaleString('zh-CN')} 行
              <span className="table-truncated">
                {' '}
                · {loadingMore ? '正在加载更多…' : '继续翻页自动加载'}
              </span>
            </>
          ) : (
            <>共 {totalCount.toLocaleString('zh-CN')} 行</>
          )}
        </span>
        {totalPages > 1 && (
          <div className="pager">
            <button
              className="pager-btn"
              disabled={safePage <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              ‹ 上一页
            </button>
            <span className="pager-info">
              第 {safePage} / {totalPages} 页
            </span>
            <button
              className="pager-btn"
              disabled={safePage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              下一页 ›
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
